import { parseIncomeYen } from './spouseEligibility';
import { isSpouseChangeReasonCode } from './spouseChangeReason';
import type {
  SpouseDeclarationSnapshot,
  SpouseSectionStep,
  SpouseSnapshot,
} from './spouseSectionTypes';
import { SPOUSE_SECTION_STEP_ORDER } from './spouseSectionTypes';

export function stepIndex(step: SpouseSectionStep): number {
  return SPOUSE_SECTION_STEP_ORDER.indexOf(step);
}

export function stepQuestionKey(step: SpouseSectionStep): string {
  return `submitDocuments.dependentDeduction.spouse.steps.${step}`;
}

export function stepHelpKey(step: SpouseSectionStep): string {
  return `submitDocuments.dependentDeduction.spouse.help.${step}`;
}

export function hasHelpText(step: SpouseSectionStep): boolean {
  return [
    'employeeEstimatedIncome',
    'spouseFamilyWorker',
    'spouseEstimatedIncome',
    'sharedLivelihood',
  ].includes(step);
}

export function nextStepAfter(
  step: SpouseSectionStep,
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot,
  opts: { hasSpouseRecord: boolean; ineligible: boolean }
): SpouseSectionStep | null {
  switch (step) {
    case 'addSpouseName':
      return 'addSpouseKana';
    case 'addSpouseKana':
      return 'addSpouseDob';
    case 'addSpouseDob':
      return 'sharedLivelihood';
    case 'sharedLivelihood':
      return parseIncomeYen(decl.employeeEstimatedIncome) > 0 ? 'spouseFamilyWorker' : 'employeeEstimatedIncome';
    case 'employeeEstimatedIncome':
      return 'spouseFamilyWorker';
    case 'spouseFamilyWorker':
      return 'spouseEstimatedIncome';
    case 'spouseEstimatedIncome':
      return opts.ineligible ? 'ineligibleNotice' : 'confirmFullName';
    case 'confirmFullName':
      return 'confirmFullNameKana';
    case 'confirmFullNameKana':
      return 'confirmMyNumber';
    case 'confirmMyNumber':
      return 'confirmDob';
    case 'confirmDob':
      return 'sameHousehold';
    case 'sameHousehold':
      return spouse.livingInSameHousehold === 'no' ? 'addressIfNot' : 'disabilityStatus';
    case 'addressIfNot':
      return 'nonResident';
    case 'nonResident':
      return spouse.nonResidentFlag === 'yes' ? 'nonResidentReason' : 'disabilityStatus';
    case 'nonResidentReason':
      return 'disabilityStatus';
    case 'disabilityStatus':
      return 'changeDuringYear';
    case 'changeDuringYear':
      return spouse.changeDuringYear === 'yes' ? 'changeDate' : null;
    case 'changeDate':
      return 'changeReason';
    case 'changeReason':
      return null;
    case 'changeReasonOther':
      return null;
    case 'ineligibleNotice':
    case 'notMarriedDone':
      return null;
    default:
      return null;
  }
}

export function firstStepForMarried(hasSpouseRecord: boolean): SpouseSectionStep {
  return hasSpouseRecord ? 'sharedLivelihood' : 'addSpouseName';
}

export function prevStepForUndo(
  step: SpouseSectionStep,
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot
): SpouseSectionStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;

  let prev = SPOUSE_SECTION_STEP_ORDER[idx - 1];

  if (step === 'disabilityStatus' && spouse.livingInSameHousehold === 'yes') {
    return 'sameHousehold';
  }
  if (step === 'disabilityStatus' && spouse.nonResidentFlag === 'no') {
    return 'nonResident';
  }
  if (step === 'nonResidentReason') {
    return 'nonResident';
  }
  if (step === 'nonResident' && spouse.livingInSameHousehold === 'no') {
    return 'addressIfNot';
  }
  if (step === 'addressIfNot') {
    return 'sameHousehold';
  }
  if (step === 'changeReasonOther') {
    return 'changeReason';
  }
  if (step === 'changeReason') {
    return 'changeDate';
  }
  if (step === 'changeDate' && spouse.changeDuringYear === 'no') {
    return 'changeDuringYear';
  }
  if (step === 'confirmFullName' && decl.sectionOutcome === 'ineligible') {
    return 'spouseEstimatedIncome';
  }
  if (step === 'ineligibleNotice') {
    return 'spouseEstimatedIncome';
  }

  while (prev && isSkippedStep(prev, spouse, decl)) {
    const pi = stepIndex(prev);
    prev = pi > 0 ? SPOUSE_SECTION_STEP_ORDER[pi - 1] : null;
  }
  return prev;
}

/** Branch steps that were skipped based on prior answers — hide from chat history. */
export function isSkippedStep(
  step: SpouseSectionStep,
  spouse: SpouseSnapshot,
  _decl: SpouseDeclarationSnapshot
): boolean {
  if (step === 'notMarriedDone') return true;
  if (step === 'addressIfNot' && spouse.livingInSameHousehold !== 'no') return true;
  if (step === 'nonResident' && spouse.livingInSameHousehold !== 'no') return true;
  if (step === 'nonResidentReason' && spouse.nonResidentFlag !== 'yes') return true;
  if (step === 'changeDate' && spouse.changeDuringYear !== 'yes') return true;
  if (step === 'changeReason' && spouse.changeDuringYear !== 'yes') return true;
  if (step === 'changeReasonOther') {
    if (spouse.changeDuringYear !== 'yes') return true;
    if (isSpouseChangeReasonCode(spouse.changeReason) && spouse.changeReason !== 'other') return true;
  }
  return false;
}

export function shouldShowStepInHistory(
  step: SpouseSectionStep,
  committed: boolean,
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot
): boolean {
  return committed && !isSkippedStep(step, spouse, decl);
}

/** Only show the ineligibility notice when the spouse is actually marked ineligible with a reason. */
export function shouldShowIneligibleInHistory(
  committed: boolean,
  decl: SpouseDeclarationSnapshot
): boolean {
  return (
    committed && decl.sectionOutcome === 'ineligible' && Boolean(decl.ineligibilityReason)
  );
}

const HOUSEHOLD_BRANCH_STEPS: SpouseSectionStep[] = [
  'sameHousehold',
  'addressIfNot',
  'nonResident',
  'nonResidentReason',
];

const CHANGE_BRANCH_STEPS: SpouseSectionStep[] = [
  'changeDuringYear',
  'changeDate',
  'changeReason',
  'changeReasonOther',
];

export function isBranchGateStep(step: SpouseSectionStep): boolean {
  return (
    step === 'sameHousehold' ||
    step === 'nonResident' ||
    step === 'changeDuringYear' ||
    step === 'changeReason'
  );
}

export function reviewStep(
  maritalStatus: 'married' | 'not_married' | '',
  decl: SpouseDeclarationSnapshot,
  spouse: SpouseSnapshot,
  opts: { changeReasonIsOther: boolean }
): SpouseSectionStep {
  if (maritalStatus === 'not_married') return 'notMarriedDone';
  if (decl.sectionOutcome === 'ineligible') return 'ineligibleNotice';
  if (spouse.changeDuringYear === 'yes') {
    return opts.changeReasonIsOther ? 'changeReasonOther' : 'changeReason';
  }
  return 'changeDuringYear';
}

export function firstMissingBranchStep(
  gateStep: SpouseSectionStep,
  spouse: SpouseSnapshot,
  opts: {
    changeReasonIsOther: boolean;
    changeReasonOtherDraft: string;
    changeReasonPendingOther?: boolean;
  }
): SpouseSectionStep | null {
  const inHouseholdBranch = HOUSEHOLD_BRANCH_STEPS.includes(gateStep);
  const inChangeBranch = CHANGE_BRANCH_STEPS.includes(gateStep);

  if (inHouseholdBranch) {
    if (spouse.livingInSameHousehold !== 'no') return null;
    if (!spouse.ifNotAddress.trim()) return 'addressIfNot';
    if (!spouse.nonResidentFlag) return 'nonResident';
    if (spouse.nonResidentFlag === 'yes' && !spouse.nonResidentReason) return 'nonResidentReason';
    return null;
  }

  if (inChangeBranch) {
    if (spouse.changeDuringYear !== 'yes') return null;
    if (!spouse.changeDate.trim()) return 'changeDate';
    if (!spouse.changeReason.trim()) {
      return opts.changeReasonPendingOther ? 'changeReasonOther' : 'changeReason';
    }
    if (
      spouse.changeReason === 'other' ||
      (opts.changeReasonIsOther && !isSpouseChangeReasonCode(spouse.changeReason))
    ) {
      const text = opts.changeReasonIsOther
        ? spouse.changeReason.trim() || opts.changeReasonOtherDraft.trim()
        : opts.changeReasonOtherDraft.trim();
      if (!text) return 'changeReasonOther';
    }
    return null;
  }

  return null;
}

export function isSpouseSectionComplete(
  step: SpouseSectionStep,
  spouse: SpouseSnapshot,
  decl: SpouseDeclarationSnapshot,
  maritalStatus: 'married' | 'not_married' | ''
): boolean {
  if (maritalStatus === 'not_married') return true;
  if (decl.sectionOutcome === 'ineligible') return step === 'ineligibleNotice';
  if (decl.sectionOutcome === 'eligible') {
    if (spouse.changeDuringYear === 'yes') return false;
    return step === 'changeDuringYear' && spouse.changeDuringYear === 'no';
  }
  return false;
}
